import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './CookieBanner.css';

const CookieBanner = ({ onClose, isOpenFromButton = false }) => {
  const { t } = useTranslation('cookiebanner');
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [cookies, setCookies] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    const cookieConsent = localStorage.getItem('mezopit_cookie_consent');
    
    if (cookieConsent) {
      try {
        const savedCookies = JSON.parse(cookieConsent);
        setCookies(savedCookies);
        
        if (isOpenFromButton) {
          setIsAnimating(true);
          setTimeout(() => setIsVisible(true), 50);
        }
      } catch (e) {
        console.error('Cookie okuma hatası:', e);
      }
    } else {
      if (isOpenFromButton) {
        setIsAnimating(true);
        setTimeout(() => setIsVisible(true), 50);
      } else {
        setIsAnimating(true);
        setTimeout(() => setIsVisible(true), 50);
      }
    }
  }, [isOpenFromButton]);

  const closeBanner = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, 300);
  };

  const handleAcceptSelected = () => {
    localStorage.setItem('mezopit_cookie_consent', JSON.stringify(cookies));
    closeBanner();
  };

  const handleAcceptAll = () => {
    const allCookies = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };

    setCookies(allCookies);
    localStorage.setItem('mezopit_cookie_consent', JSON.stringify(allCookies));
    closeBanner();
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    
    setCookies(onlyNecessary);
    localStorage.setItem('mezopit_cookie_consent', JSON.stringify(onlyNecessary));
    closeBanner();
  };

  const toggleCookie = (type) => {
    if (type === 'necessary') return;
    setCookies(prev => {
      const yeni = {
        ...prev,
        [type]: !prev[type]
      };
      return yeni;
    });
  };

  const cookieTypes = [
    {
      id: 'necessary',
      title: t('types.necessary.title'),
      icon: '🔒',
      description: t('types.necessary.description'),
      required: true
    },
    {
      id: 'analytics',
      title: t('types.analytics.title'),
      icon: '📊',
      description: t('types.analytics.description'),
      required: false
    },
    {
      id: 'marketing',
      title: t('types.marketing.title'),
      icon: '🎯',
      description: t('types.marketing.description'),
      required: false
    },
    {
      id: 'preferences',
      title: t('types.preferences.title'),
      icon: '👤',
      description: t('types.preferences.description'),
      required: false
    }
  ];

  const CookieDetails = () => (
    <div className="cookie-details">
      <div className="cookie-details-header">
        <span className="details-icon">ℹ️</span>
        <h4>{t('details.title')}</h4>
      </div>
      
      {cookieTypes.map((type) => (
        <div className="cookie-type" key={type.id}>
          <div className="cookie-type-header">
            <div className="cookie-type-title-wrapper">
              <span className="cookie-type-icon">{type.icon}</span>
              <span className="cookie-type-title">
                <strong>{type.title}</strong>
              </span>
            </div>
            <div className="cookie-toggle-wrapper">
              {type.required ? (
                <div className="cookie-badge required">{t('badge.required')}</div>
              ) : (
                <div className="cookie-toggle">
                  <input 
                    type="checkbox" 
                    checked={cookies[type.id]}
                    onChange={() => toggleCookie(type.id)}
                    id={`${type.id}-cookie`}
                  />
                  <label 
                    htmlFor={`${type.id}-cookie`} 
                    className="cookie-toggle-label"
                  >
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              )}
            </div>
          </div>
          <p className="cookie-type-desc">
            {type.description}
          </p>
        </div>
      ))}
    </div>
  );

  if (!isVisible) return null;

  return (
    <div className={`cookie-banner-overlay ${isAnimating ? 'visible' : ''}`}>
      <div className={`cookie-banner ${isAnimating ? 'popup-show' : 'popup-hide'}`}>
        <div className="cookie-header">
          <div className="cookie-icon-wrapper">
            <span className="cookie-main-icon">🍪</span>
          </div>
          <div className="cookie-header-content">
            <h3 className="cookie-title">
              {isOpenFromButton ? t('title.edit') : t('title.default')}
            </h3>
            <p className="cookie-subtitle">
              {isOpenFromButton 
                ? t('subtitle.edit')
                : t('subtitle.default')
              }
            </p>
          </div>
        </div>

        <div className="cookie-content">
          <div className="cookie-description-container">
            <span className="description-icon"></span>
            <p className="cookie-description">
              {t('description')}{' '}
              <Link to="/veri-güvenligi-politikamiz" className="privacy-link" onClick={closeBanner}>
                {t('privacyLink')}
              </Link>
              {t('descriptionEnd')}
            </p>
          </div>

          {showDetails && <CookieDetails />}

          <div className="cookie-buttons">
            <button 
              className="cookie-btn cookie-btn-reject"
              onClick={handleRejectAll}
            >
              <span className="btn-icon">✖</span>
              <span>{t('buttons.rejectAll')}</span>
            </button>
            
            <button 
              className="cookie-btn cookie-btn-settings"
              onClick={() => setShowDetails(!showDetails)}
            >
              <span className="btn-icon">⚙️</span>
              <span>
                {showDetails ? t('buttons.hideSettings') : t('buttons.showSettings')}
                <span className="chevron-icon">
                  {showDetails ? '▲' : '▼'}
                </span>
              </span>
            </button>
            
            <button 
              className="cookie-btn cookie-btn-accept"
              onClick={handleAcceptSelected}
            >
              <span className="btn-icon">✓</span>
              <span>{t('buttons.acceptSelected')}</span>
            </button>
            
            <button 
              className="cookie-btn cookie-btn-all"
              onClick={handleAcceptAll}
            >
              <span className="btn-icon">✓✓</span>
              <span>{t('buttons.acceptAll')}</span>
            </button>
          </div>
          
          <div className="cookie-footer-links">
            <div className="footer-links-container">
              <Link to="/veri-güvenligi-politikamiz" className="footer-link" onClick={closeBanner}>
                <span className="footer-icon">🛡️</span>
                <span>{t('footer.dataSecurity')}</span>
              </Link>
              <Link to="/cerez-politikamiz" className="footer-link" onClick={closeBanner}>
                <span className="footer-icon">🍪</span>
                <span>{t('footer.cookiePolicy')}</span>
              </Link>
              <Link to="/gizlilik-politikamiz" className="footer-link" onClick={closeBanner}>
                <span className="footer-icon">📄</span>
                <span>{t('footer.privacyPolicy')}</span>
              </Link>
            </div>
            <div className="cookie-learn-more">
              <Link to="/veri-güvenligi-politikamiz" className="learn-more-link" onClick={closeBanner}>
                {t('footer.learnMore')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;